import Tooltip, { useAnalysisActiveDot } from './AnalysisTooltip';
import React from 'react';
import { Checkbox } from './pilot-ui/checkbox';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { ChartData, ChartSeries } from '../types';
import NoteMarkerOverlay, { SeriesNotes } from './NoteMarkerOverlay';
import ChartCsvButton from './ChartCsvButton';
import { CsvColumn } from '../utils/chartCsv';
import { DATA_LINE_Z_INDEX, REFERENCE_LINE_Z_INDEX } from './referenceLineLayer';
import { ANALYSIS_CHART_LEFT_MARGIN, ANALYSIS_CHART_RIGHT_MARGIN, ANALYSIS_LEGEND_WRAPPER_STYLE, DEFAULT_Y_AXIS_WIDTH, TEMP_HUMIDITY_ALERT_LABEL_STRIP_PX } from './analysisChartLayout';
import {
  HUMIDITY_ALERT_HIGH_PCT,
  HUMIDITY_ALERT_LOW_PCT,
  TEMP_URGENT_HIGH_F,
  TEMP_URGENT_LOW_F,
  TEMP_WARNING_HIGH_F,
  TEMP_WARNING_LOW_F,
} from '../utils/alertBounds';

interface TempHumidityChartProps {
  jsonData: ChartData;
  overlaySeries?: ChartSeries[];
  noteSeries?: SeriesNotes[];
  onNoteImageClick?: (noteId: number) => void;
}

const toFahrenheit = (celsius: number) => (celsius * 9) / 5 + 32;


type AlertLine = {
  axis: 'temperature' | 'humidity';
  y: number;
  label: string;
  side: 'high' | 'low';
  urgent: boolean;
};

const ALERT_LINES: AlertLine[] = [
  { axis: 'humidity', y: HUMIDITY_ALERT_HIGH_PCT, label: `High humidity ${HUMIDITY_ALERT_HIGH_PCT}%`, side: 'high', urgent: false },
  { axis: 'humidity', y: HUMIDITY_ALERT_LOW_PCT, label: `Low humidity ${HUMIDITY_ALERT_LOW_PCT}%`, side: 'low', urgent: false },
  { axis: 'temperature', y: TEMP_WARNING_HIGH_F, label: `High temp ${TEMP_WARNING_HIGH_F}°F`, side: 'high', urgent: false },
  { axis: 'temperature', y: TEMP_WARNING_LOW_F, label: `Low temp ${TEMP_WARNING_LOW_F}°F`, side: 'low', urgent: false },
  { axis: 'temperature', y: TEMP_URGENT_HIGH_F, label: `Urgent temp ${TEMP_URGENT_HIGH_F}°F`, side: 'high', urgent: true },
  { axis: 'temperature', y: TEMP_URGENT_LOW_F, label: `Urgent temp ${TEMP_URGENT_LOW_F}°F`, side: 'low', urgent: true },
];

const HUMIDITY_AXIS_MIN = 0;
const HUMIDITY_AXIS_MAX = 100;

/** FS-621: which of the six return-side series are drawn on the single-device
 *  chart. Colour carries the measurement (temperature red, humidity blue) and
 *  shade plus line pattern carry the statistic, so all six stay tellable apart
 *  with everything switched on.
 *
 *  Deliberately NOT applied to the multi-device overlay: there a line's colour
 *  already says which pinned device it belongs to, so the red/blue rule cannot
 *  hold, and six series per device would put eighteen lines on one panel.
 */
type SeriesField =
  | 'm_temperatures'
  | 'min_temperatures'
  | 'max_temperatures'
  | 'm_humidities'
  | 'min_humidities'
  | 'max_humidities';

type StatSeries = {
  /** localStorage key; also the checkbox's test id. */
  key: string;
  /** Field on the chart payload. */
  field: SeriesField;
  /** Legend/tooltip name. Must contain "Temp" or "Humidity" — the tooltip
   *  formatter picks the unit off the name. */
  name: string;
  /** Checkbox caption. */
  label: string;
  axis: 'temperature' | 'humidity';
  color: string;
  /** undefined = solid. */
  dash?: string;
  /** On when the viewer has no stored preference. */
  defaultOn: boolean;
};

const TEMPERATURE_SERIES: StatSeries[] = [
  {
    key: 'tempHumidityChart.temperature.min',
    field: 'min_temperatures',
    name: 'Temperature (min)',
    label: 'Min',
    axis: 'temperature',
    color: '#7f1d1d',
    dash: '2 3',
    defaultOn: false,
  },
  {
    key: 'tempHumidityChart.temperature.avg',
    field: 'm_temperatures',
    name: 'Temperature (avg)',
    label: 'Average',
    axis: 'temperature',
    color: '#dc2626',
    defaultOn: true,
  },
  {
    key: 'tempHumidityChart.temperature.max',
    field: 'max_temperatures',
    name: 'Temperature (max)',
    label: 'Max',
    axis: 'temperature',
    color: '#f87171',
    dash: '6 3',
    defaultOn: false,
  },
];

const HUMIDITY_SERIES: StatSeries[] = [
  {
    key: 'tempHumidityChart.humidity.min',
    field: 'min_humidities',
    name: 'Humidity (min)',
    label: 'Min',
    axis: 'humidity',
    color: '#1e3a8a',
    dash: '2 3',
    defaultOn: false,
  },
  {
    key: 'tempHumidityChart.humidity.avg',
    field: 'm_humidities',
    name: 'Humidity (avg)',
    label: 'Average',
    axis: 'humidity',
    color: '#2563eb',
    defaultOn: true,
  },
  {
    key: 'tempHumidityChart.humidity.max',
    field: 'max_humidities',
    name: 'Humidity (max)',
    label: 'Max',
    axis: 'humidity',
    color: '#60a5fa',
    dash: '6 3',
    defaultOn: false,
  },
];

const ALL_STAT_SERIES = [...TEMPERATURE_SERIES, ...HUMIDITY_SERIES];

/** FS-826: the tooltip lists entries in drawing order (min, avg, max), which
 *  reads upside down against the lines. Rank them max, avg, min so the popup
 *  matches where the lines sit, temperature above humidity. */
const TOOLTIP_ORDER = [...TEMPERATURE_SERIES].reverse()
  .concat([...HUMIDITY_SERIES].reverse())
  .map((s) => s.key);
const soloTooltipRank = (item: { dataKey?: unknown }) => TOOLTIP_ORDER.indexOf(String(item.dataKey));

/** Reads the six remembered checkbox states. Storage can throw outright in
 *  private-browsing mode, so a failure falls back to the defaults rather than
 *  taking the chart down with it. */
const readSelection = (): Record<string, boolean> => {
  const selection: Record<string, boolean> = {};
  ALL_STAT_SERIES.forEach((s) => {
    selection[s.key] = s.defaultOn;
  });
  try {
    ALL_STAT_SERIES.forEach((s) => {
      const stored = localStorage.getItem(s.key);
      if (stored !== null) selection[s.key] = stored === '1';
    });
  } catch {
    // storage unavailable — every checkbox keeps its default
  }
  return selection;
};

const TempHumidityChart: React.FC<TempHumidityChartProps> = ({
  jsonData,
  overlaySeries,
  noteSeries,
  onNoteImageClick,
}) => {
  const isOverlay = !!overlaySeries && overlaySeries.length > 1;
  const [selection, setSelection] = React.useState<Record<string, boolean>>(readSelection);
  const activeDot = useAnalysisActiveDot(true);

  const toggleSeries = (key: string) => {
    const next = !selection[key];
    setSelection((prev) => ({ ...prev, [key]: next }));
    try {
      localStorage.setItem(key, next ? '1' : '0');
    } catch {
      // storage unavailable (private mode) — preference just won't persist
    }
  };

  const series: ChartSeries[] = isOverlay
    ? overlaySeries!
    : [{ id: 'solo', label: 'Sensor', color: '#dc2626', data: jsonData }];

  if (!series[0]?.data) return null;

  // If every series is empty, fall back to the original "no data" message.
  const anySeriesHasData = series.some((s) => {
    const d = s.data;
    const n = Math.min(d.dates?.length ?? 0, d.m_temperatures?.length ?? 0, d.m_humidities?.length ?? 0);
    return n > 0;
  });
  if (!anySeriesHasData) {
    return <div className="border p-4 rounded-md">No internal temperature and humidity data</div>;
  }

  const allDatesSet = new Set<string>();
  series.forEach((s) => (s.data.dates ?? []).forEach((d) => allDatesSet.add(d)));
  const allDates = Array.from(allDatesSet).sort();

  // FS-621: on the single-device chart the selected min/max/average series are
  // drawn instead of the fixed pair. The overlay keeps the two averages.
  // The rows carry every series with data so the CSV has all six; the
  // checkboxes only choose which of them are drawn.
  const soloAvailable = isOverlay
    ? []
    : ALL_STAT_SERIES.filter((s) => {
      const values: (number | null)[] | undefined = jsonData[s.field];
      return Array.isArray(values) && values.some((v) => v !== null && v !== undefined);
    });
  const soloSeries = soloAvailable.filter((s) => selection[s.key]);
  const drawnAxes = new Set<AlertLine['axis']>(
    isOverlay ? ['temperature', 'humidity'] : soloSeries.map((s) => s.axis),
  );
  const alertLines = ALERT_LINES.filter((a) => drawnAxes.has(a.axis));

  const chartData = allDates.map((date) => {
    const row: Record<string, string | number | null> = { date };
    if (isOverlay) {
      series.forEach((s) => {
        const i = s.data.dates?.indexOf(date) ?? -1;
        const temp = i >= 0 ? s.data.m_temperatures?.[i] : null;
        const hum = i >= 0 ? s.data.m_humidities?.[i] : null;
        row[`temperature_${s.id}`] = typeof temp === 'number' ? toFahrenheit(temp) : null;
        row[`humidity_${s.id}`] = typeof hum === 'number' ? hum : null;
      });
    } else {
      const i = jsonData.dates?.indexOf(date) ?? -1;
      soloAvailable.forEach((s) => {
        const values: (number | null)[] | undefined = jsonData[s.field];
        const value = i >= 0 ? values?.[i] : null;
        row[s.key] = typeof value === 'number'
          ? (s.axis === 'temperature' ? toFahrenheit(value) : value)
          : null;
      });
    }
    return row;
  });

  const unit = (axis: StatSeries['axis']) => (axis === 'temperature' ? '°F' : '%');
  const csvColumns: CsvColumn<(typeof chartData)[number]>[] = [
    { header: 'Date', value: 'date' },
    ...(isOverlay
      ? series.flatMap((s) => [
        { header: `${s.label} — Temperature avg (°F)`, value: `temperature_${s.id}` },
        { header: `${s.label} — Humidity avg (%)`, value: `humidity_${s.id}` },
      ])
      : soloAvailable.map((s) => ({ header: `${s.name} (${unit(s.axis)})`, value: s.key }))),
  ];

  const formatTooltip = (value: ValueType | undefined, name: NameType | undefined): [React.ReactNode, NameType] => {
    const seriesName = name ?? '';
    if (value === undefined) return ['-', seriesName];
    if (typeof seriesName === 'string' && seriesName.includes('Temp')) {
      const temp = typeof value === 'number' ? value.toFixed(1) : value;
      return [`${temp}°F`, seriesName];
    }
    if (typeof seriesName === 'string' && seriesName.includes('Humidity')) {
      const humidity = typeof value === 'number' ? value.toFixed(1) : value;
      return [`${humidity}%`, seriesName];
    }
    return [value, seriesName];
  };

  const renderCheckboxRow = (label: string, group: StatSeries[]) => (
    <div className="flex items-center gap-4">
      <span className="text-xs text-muted-foreground w-24">{label}</span>
      {group.map((s) => (
        <label
          key={s.key}
          className="flex items-center gap-1.5 text-xs text-muted-foreground select-none cursor-pointer"
        >
          <Checkbox
            className="size-6"
            checked={!!selection[s.key]}
            onCheckedChange={() => toggleSeries(s.key)}
            title={`Show or hide the ${s.label} ${label.toLowerCase()} series`}
            aria-label={`${label} ${s.label}`}
            data-testid={s.key}
          />
          {s.label}
        </label>
      ))}
    </div>
  );

  return (
    <div className="w-full mt-4">
      <div className="flex items-start justify-between gap-2 mb-8">
        <h3 className="text-lg font-semibold">Return Side Temperature and Humidity</h3>
        <ChartCsvButton chart="temperature-humidity" rows={chartData} columns={csvColumns} />
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1" style={{ height: '400px' }}>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{
              top: 10,
              right: ANALYSIS_CHART_RIGHT_MARGIN + (drawnAxes.has('humidity') ? 0 : DEFAULT_Y_AXIS_WIDTH),
              left: ANALYSIS_CHART_LEFT_MARGIN + (drawnAxes.has('temperature') ? 0 : DEFAULT_Y_AXIS_WIDTH),
              bottom: 60,
            }}>
              {drawnAxes.size > 0 && <CartesianGrid
                strokeDasharray="3 3"
                yAxisId={drawnAxes.has('temperature') ? 'temperature' : 'humidity'}
              />}
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={50}
                padding={{ left: 0, right: TEMP_HUMIDITY_ALERT_LABEL_STRIP_PX }}
              />
              {drawnAxes.has('temperature') && <YAxis
                yAxisId="temperature"
                domain={['auto', 'auto']}
                label={{ value: 'Temperature (°F)', angle: -90, position: 'insideLeft', offset: -35 }}
              />}
              {drawnAxes.has('humidity') && <YAxis
                yAxisId="humidity"
                orientation="right"
                domain={[HUMIDITY_AXIS_MIN, HUMIDITY_AXIS_MAX]}
                label={{ value: 'Humidity (%)', angle: 90, position: 'insideRight', offset: -35 }}
              />}
              <Tooltip formatter={formatTooltip} itemSorter={isOverlay ? undefined : soloTooltipRank} />
              <Legend verticalAlign="top" height={20} itemSorter={null} wrapperStyle={ANALYSIS_LEGEND_WRAPPER_STYLE} />
              {alertLines.map((a) => (
                // Warning lines stretch the axis so they are always in view; urgent
                // lines show only once the data already reaches them. Labels face
                // into the band so a line at the axis edge keeps its label on screen.
                <ReferenceLine
                  key={a.label}
                  zIndex={REFERENCE_LINE_Z_INDEX}
                  y={a.y}
                  yAxisId={a.axis}
                  stroke={a.urgent ? 'red' : 'orange'}
                  strokeDasharray="3 3"
                  ifOverflow={a.urgent ? 'discard' : 'extendDomain'}
                  label={{
                    value: a.label,
                    position: a.side === 'high' ? 'insideTopRight' : 'insideBottomRight',
                    fontSize: 11,
                  }}
                />
              ))}
              {isOverlay && series.map((s) => (
                <Line
                  key={`temperature_${s.id}`}
                  zIndex={DATA_LINE_Z_INDEX}
                  type="monotone"
                  dataKey={`temperature_${s.id}`}
                  stroke={s.color}
                  dot={{ r: 2 }}
                  activeDot={activeDot}
                  name={`${s.label} — Temperature`}
                  yAxisId="temperature"
                  strokeWidth={2}
                  connectNulls={true}
                />
              ))}
              {isOverlay && series.map((s) => (
                <Line
                  key={`humidity_${s.id}`}
                  zIndex={DATA_LINE_Z_INDEX}
                  type="monotone"
                  dataKey={`humidity_${s.id}`}
                  stroke={s.color}
                  strokeDasharray="4 2"
                  dot={{ r: 2 }}
                  activeDot={activeDot}
                  name={`${s.label} — Humidity`}
                  yAxisId="humidity"
                  strokeWidth={2}
                  connectNulls={true}
                />
              ))}
              {!isOverlay && soloSeries.map((s) => (
                <Line
                  key={s.key}
                  zIndex={DATA_LINE_Z_INDEX}
                  type="monotone"
                  dataKey={s.key}
                  stroke={s.color}
                  strokeDasharray={s.dash}
                  dot={{ r: 2 }}
                  activeDot={activeDot}
                  name={s.name}
                  yAxisId={s.axis}
                  strokeWidth={2}
                  connectNulls={true}
                />
              ))}
              {noteSeries && noteSeries.length > 0 && onNoteImageClick && (
                <NoteMarkerOverlay seriesNotes={noteSeries} onImageClick={onNoteImageClick} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {!isOverlay && (
        <div className="mt-2 flex flex-col gap-1">
          {renderCheckboxRow('Temperature', TEMPERATURE_SERIES)}
          {renderCheckboxRow('Humidity', HUMIDITY_SERIES)}
        </div>
      )}
    </div>
  );
};

export default TempHumidityChart;
